import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    FiUploadCloud,
    FiFile,
    FiEdit2,
    FiTrash2,
    FiCheck,
    FiX,
    FiDownload,
} from "react-icons/fi";
import Swal from "sweetalert2";
import Modal from "../../components/Modal";
import { answerDocumentsApi } from "../../services/api";

/**
 * Reviewer "Upload Reference Documents" modal — React equivalent of the Blazor
 * ManageAnswerDocuments dialog. Lists the documents attached to an assessment
 * answer and lets the reviewer upload, edit metadata, download and delete them.
 *
 * Reports the current document count back through onClose so the caller can
 * refresh its badge (mirrors the Blazor dialog returning the count on Done).
 */
const ManageAnswerDocumentsModal = ({
    isOpen,
    assessmentId,
    answerId,
    onClose,
}) => {
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [editForm, setEditForm] = useState({});
    const fileInputRef = useRef(null);

    const canLoad = isOpen && assessmentId != null && answerId != null;

    const loadDocuments = useCallback(async () => {
        if (!canLoad) return;
        setLoading(true);
        setError(null);
        try {
            const docs = await answerDocumentsApi.getForAnswer(
                assessmentId,
                answerId,
            );
            setDocuments(Array.isArray(docs) ? docs : []);
        } catch (err) {
            console.error("Failed to load answer documents", err);
            setError(err.message || "Failed to load documents.");
        } finally {
            setLoading(false);
        }
    }, [canLoad, assessmentId, answerId]);

    useEffect(() => {
        if (isOpen) {
            setEditingId(null);
            loadDocuments();
        }
    }, [isOpen, loadDocuments]);

    // ── Upload ──────────────────────────────────────────────────────────────
    const handleFilesSelected = async (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;
        setUploading(true);
        setError(null);
        try {
            // Upload sequentially so a failure surfaces the offending file.
            for (const file of files) {
                await answerDocumentsApi.upload(assessmentId, answerId, file);
            }
            await loadDocuments();
        } catch (err) {
            console.error("Upload failed", err);
            setError(err.message || "Upload failed.");
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    // ── Inline edit ─────────────────────────────────────────────────────────
    const startEdit = (doc) => {
        setEditingId(doc.refDocId);
        setEditForm({
            docTitle: doc.docTitle || "",
            docType: doc.docType || "",
            description: doc.description || "",
            notes: doc.notes || "",
        });
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditForm({});
    };

    const saveEdit = async (doc) => {
        try {
            await answerDocumentsApi.update(doc.refDocId, {
                docTitle: editForm.docTitle,
                docType: editForm.docType,
                description: editForm.description,
                notes: editForm.notes,
                status: doc.status ?? null,
            });
            cancelEdit();
            await loadDocuments();
        } catch (err) {
            console.error("Update failed", err);
            setError(err.message || "Failed to update document.");
        }
    };

    // ── Download / view ──────────────────────────────────────────────────────
    const handleDownload = async (doc) => {
        try {
            const { url } = await answerDocumentsApi.getDownloadUrl(
                doc.refDocId,
            );
            window.open(url, "_blank", "noopener,noreferrer");
        } catch (err) {
            console.error("Failed to open document", err);
            setError(err.message || "Failed to open document.");
        }
    };

    // ── Delete ────────────────────────────────────────────────────────────────
    const handleDelete = async (doc) => {
        const result = await Swal.fire({
            title: "Delete document?",
            text: `Are you sure you want to delete "${doc.docTitle || "this document"}"?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#F47F2E",
            cancelButtonColor: "#919191",
            confirmButtonText: "Yes, delete",
            cancelButtonText: "Cancel",
            background: "#ffffff",
            color: "#53545a",
            iconColor: "#F47F2E",
        });
        if (!result.isConfirmed) return;

        try {
            await answerDocumentsApi.delete(doc.refDocId);
            await loadDocuments();
        } catch (err) {
            console.error("Delete failed", err);
            setError(err.message || "Failed to delete document.");
        }
    };

    const handleClose = () => {
        onClose?.(documents.length);
    };

    const footer = (
        <button
            onClick={handleClose}
            className="px-5 py-1.5 text-sm font-semibold bg-[#0077b5] text-white hover:bg-[#0077b5]/90 transition-colors"
        >
            Done
        </button>
    );

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title="Upload Assessment Answer Documents"
            footer={footer}
            size="xl"
        >
            {/* Upload dropzone */}
            <label
                className={`flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-6 mb-4 cursor-pointer transition-colors ${
                    uploading
                        ? "border-[#0077b5] bg-[#f0f7ff]"
                        : "border-[#919191] hover:border-[#0077b5] hover:bg-[#f7f8fa]"
                }`}
            >
                <FiUploadCloud className="text-3xl text-[#0077b5] mb-2" />
                <span className="text-sm font-medium text-[#53545a]">
                    {uploading ? "Uploading…" : "Choose files to upload"}
                </span>
                <span className="text-xs text-[#919191] mt-1">
                    PDF, DOCX, XLSX, images, TXT, CSV · up to 32 MB each
                </span>
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    disabled={uploading}
                    onChange={handleFilesSelected}
                />
            </label>

            {error && (
                <div className="mb-3 flex items-start gap-2 bg-[#fdf0e8] border border-[#F47F2E]/40 text-[#a8471b] text-xs px-3 py-2 rounded">
                    <FiX className="mt-0.5 flex-shrink-0" />
                    <span>{error}</span>
                </div>
            )}

            {/* Documents table */}
            <div className="border border-[#e0e2e6] rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-[#f8f9fa] text-left text-[10px] uppercase tracking-wide text-[#919191]">
                            <th className="px-3 py-2 font-bold">Document</th>
                            <th className="px-3 py-2 font-bold">Doc Type</th>
                            <th className="px-3 py-2 font-bold">Description</th>
                            <th className="px-3 py-2 font-bold">Notes</th>
                            <th className="px-3 py-2 font-bold text-right">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td
                                    colSpan={5}
                                    className="px-3 py-8 text-center text-[#919191]"
                                >
                                    Loading documents…
                                </td>
                            </tr>
                        ) : documents.length === 0 ? (
                            <tr>
                                <td
                                    colSpan={5}
                                    className="px-3 py-8 text-center text-[#919191]"
                                >
                                    No documents attached yet.
                                </td>
                            </tr>
                        ) : (
                            documents.map((doc) => {
                                const isEditing = editingId === doc.refDocId;
                                return (
                                    <tr
                                        key={doc.refDocId}
                                        className="border-t border-[#e0e2e6] hover:bg-[#f7f8fa] align-top"
                                    >
                                        {/* Document name */}
                                        <td className="px-3 py-2">
                                            {isEditing ? (
                                                <input
                                                    value={editForm.docTitle}
                                                    onChange={(e) =>
                                                        setEditForm((f) => ({
                                                            ...f,
                                                            docTitle:
                                                                e.target.value,
                                                        }))
                                                    }
                                                    className="w-full border border-[#919191] rounded px-2 py-1 text-xs outline-none focus:border-[#0077b5]"
                                                />
                                            ) : (
                                                <button
                                                    onClick={() =>
                                                        handleDownload(doc)
                                                    }
                                                    title="Open document"
                                                    className="inline-flex items-center gap-1.5 text-[#0077b5] hover:text-[#005a8f] transition-colors max-w-[200px]"
                                                >
                                                    <FiFile className="flex-shrink-0" />
                                                    <span className="truncate">
                                                        {doc.docTitle ||
                                                            doc.refDoc
                                                                ?.split("/")
                                                                .pop() ||
                                                            "Document"}
                                                    </span>
                                                </button>
                                            )}
                                        </td>
                                        {/* Doc type */}
                                        <td className="px-3 py-2 text-[#53545a]">
                                            {isEditing ? (
                                                <input
                                                    value={editForm.docType}
                                                    onChange={(e) =>
                                                        setEditForm((f) => ({
                                                            ...f,
                                                            docType:
                                                                e.target.value,
                                                        }))
                                                    }
                                                    className="w-full border border-[#919191] rounded px-2 py-1 text-xs outline-none focus:border-[#0077b5]"
                                                />
                                            ) : (
                                                doc.docType || "—"
                                            )}
                                        </td>
                                        {/* Description */}
                                        <td className="px-3 py-2 text-[#53545a]">
                                            {isEditing ? (
                                                <input
                                                    value={editForm.description}
                                                    onChange={(e) =>
                                                        setEditForm((f) => ({
                                                            ...f,
                                                            description:
                                                                e.target.value,
                                                        }))
                                                    }
                                                    className="w-full border border-[#919191] rounded px-2 py-1 text-xs outline-none focus:border-[#0077b5]"
                                                />
                                            ) : (
                                                doc.description || "—"
                                            )}
                                        </td>
                                        {/* Notes */}
                                        <td className="px-3 py-2 text-[#53545a]">
                                            {isEditing ? (
                                                <input
                                                    value={editForm.notes}
                                                    onChange={(e) =>
                                                        setEditForm((f) => ({
                                                            ...f,
                                                            notes: e.target
                                                                .value,
                                                        }))
                                                    }
                                                    className="w-full border border-[#919191] rounded px-2 py-1 text-xs outline-none focus:border-[#0077b5]"
                                                />
                                            ) : (
                                                doc.notes || "—"
                                            )}
                                        </td>
                                        {/* Actions */}
                                        <td className="px-3 py-2">
                                            <div className="flex items-center justify-end gap-1.5">
                                                {isEditing ? (
                                                    <>
                                                        <button
                                                            onClick={() =>
                                                                saveEdit(doc)
                                                            }
                                                            title="Save"
                                                            className="p-1.5 rounded text-[#0077b5] hover:bg-[#0077b5]/10 transition-colors"
                                                        >
                                                            <FiCheck />
                                                        </button>
                                                        <button
                                                            onClick={cancelEdit}
                                                            title="Cancel"
                                                            className="p-1.5 rounded text-[#919191] hover:bg-[#919191]/10 transition-colors"
                                                        >
                                                            <FiX />
                                                        </button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <button
                                                            onClick={() =>
                                                                handleDownload(
                                                                    doc,
                                                                )
                                                            }
                                                            title="Download"
                                                            className="p-1.5 rounded text-[#0077b5] hover:bg-[#0077b5]/10 transition-colors"
                                                        >
                                                            <FiDownload />
                                                        </button>
                                                        <button
                                                            onClick={() =>
                                                                startEdit(doc)
                                                            }
                                                            title="Edit"
                                                            className="p-1.5 rounded text-[#0077b5] hover:bg-[#0077b5]/10 transition-colors"
                                                        >
                                                            <FiEdit2 />
                                                        </button>
                                                        <button
                                                            onClick={() =>
                                                                handleDelete(doc)
                                                            }
                                                            title="Delete"
                                                            className="p-1.5 rounded text-[#F47F2E] hover:bg-[#F47F2E]/10 transition-colors"
                                                        >
                                                            <FiTrash2 />
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </Modal>
    );
};

export default ManageAnswerDocumentsModal;
